import { useState, useEffect } from 'react'
import './App.css'

// Bread recipes with accurate baker's percentages
const breadRecipes = {
  'White Bread': {
    hydration: 65,
    yeast: 1.5,
    salt: 2.0,
    sugar: 3,
    butter: 3,
    description: 'Classic soft white bread, perfect for sandwiches',
    color: '#f5deb3'
  },
  'Whole Wheat': {
    hydration: 78,
    yeast: 1.8,
    salt: 2.0,
    sugar: 2,
    butter: 2,
    description: 'Nutritious whole grain bread with nutty flavor',
    color: '#d2691e'
  },
  'Sourdough': {
    hydration: 75,
    yeast: 0, // uses starter instead
    salt: 2.0,
    sugar: 0,
    butter: 0,
    starter: 20, // sourdough starter
    description: 'Tangy artisan bread with complex flavors',
    color: '#daa520'
  },
  'Baguette': {
    hydration: 68,
    yeast: 0.8,
    salt: 2.0,
    sugar: 0,
    butter: 0,
    description: 'Classic French bread with crispy crust',
    color: '#f4a460'
  },
  'Ciabatta': {
    hydration: 78,
    yeast: 0.5,
    salt: 2.0,
    sugar: 0,
    butter: 0,
    oil: 2,
    description: 'Italian bread with open crumb and chewy texture',
    color: '#deb887'
  },
  'Focaccia': {
    hydration: 75,
    yeast: 1.0,
    salt: 2.0,
    sugar: 1,
    butter: 0,
    oil: 8,
    description: 'Flat Italian bread perfect for herbs and toppings',
    color: '#f0e68c'
  },
  'Rye Bread': {
    hydration: 100,
    yeast: 1.5,
    salt: 2.0,
    sugar: 2,
    butter: 1,
    description: 'Dense, flavorful bread with distinctive taste',
    color: '#8b4513'
  },
  'Brioche': {
    hydration: 50,
    yeast: 2.0,
    salt: 1.8,
    sugar: 12,
    butter: 50,
    eggs: 50,
    description: 'Rich, buttery French bread with soft texture',
    color: '#ffd700'
  },
  'Challah': {
    hydration: 55,
    yeast: 1.5,
    salt: 2.0,
    sugar: 8,
    butter: 0,
    oil: 8,
    eggs: 30,
    description: 'Traditional Jewish bread, slightly sweet and rich',
    color: '#f5c542'
  },
  'Pumpernickel': {
    hydration: 95,
    yeast: 1.0,
    salt: 2.0,
    sugar: 0,
    butter: 0,
    molasses: 8,
    description: 'Dark German rye bread with deep, earthy flavor',
    color: '#654321'
  },
  'Pita Bread': {
    hydration: 60,
    yeast: 1.2,
    salt: 2.0,
    sugar: 1,
    butter: 0,
    oil: 3,
    description: 'Middle Eastern flatbread that puffs into a pocket',
    color: '#f5e6d3'
  },
  'Naan': {
    hydration: 55,
    yeast: 1.5,
    salt: 1.5,
    sugar: 3,
    butter: 5,
    yogurt: 20,
    description: 'Soft Indian flatbread traditionally cooked in tandoor',
    color: '#ffe4b5'
  }
}

// Define ingredient measurement types
const INGREDIENT_UNITS = {
  flour: 'weight',
  water: 'volume',
  yeast: 'weight',
  salt: 'weight',
  sugar: 'weight',
  butter: 'weight',
  oil: 'volume',
  eggs: 'count',
  starter: 'weight',
  yogurt: 'volume',
  molasses: 'volume'
}

// Average egg weight in grams (for conversion)
const AVG_EGG_WEIGHT = 50

// Density conversions (g to ml) - approximate values for baking
const DENSITIES = {
  water: 1.0,      // 1g = 1ml
  oil: 0.92,       // 1g = 1.09ml (oil is lighter than water)
  yogurt: 1.03,    // 1g = 0.97ml (slightly denser than water)
  molasses: 1.4,   // 1g = 0.71ml (much denser than water)
  yeast: 0.64,     // 1g = 1.56ml (active dry yeast is light)
  salt: 1.22,      // 1g = 0.82ml (table salt is denser)
  sugar: 0.85,     // 1g = 1.18ml (granulated sugar)
  butter: 0.96,    // 1g = 1.04ml (softened butter)
  starter: 1.0,    // 1g = 1ml (sourdough starter, similar to water)
  flour: 0.59      // 1g = 1.69ml (all-purpose flour is very light)
}

function App() {
  const [selectedBread, setSelectedBread] = useState('White Bread')
  const [flourAmount, setFlourAmount] = useState(500)
  const [ingredients, setIngredients] = useState({})
  const [feedback, setFeedback] = useState([])
  const [useML, setUseML] = useState(false)

  useEffect(() => {
    calculateIngredients()
    setFeedback([])
  }, [selectedBread, flourAmount])

  const calculateIngredients = () => {
    const recipe = breadRecipes[selectedBread]
    const flour = flourAmount

    const calculated = {
      flour: flour,
      water: Math.round(flour * (recipe.hydration / 100)),
      yeast: (flour * (recipe.yeast / 100)).toFixed(1),
      salt: (flour * (recipe.salt / 100)).toFixed(1)
    }

    if (recipe.sugar > 0) calculated.sugar = (flour * (recipe.sugar / 100)).toFixed(1)
    if (recipe.butter > 0) calculated.butter = Math.round(flour * (recipe.butter / 100))
    if (recipe.oil > 0) calculated.oil = (flour * (recipe.oil / 100)).toFixed(1)
    if (recipe.eggs > 0) {
      // Convert egg weight to count
      const eggWeight = flour * (recipe.eggs / 100)
      calculated.eggs = Math.round(eggWeight / AVG_EGG_WEIGHT)
    }
    if (recipe.starter > 0) calculated.starter = Math.round(flour * (recipe.starter / 100))
    if (recipe.yogurt > 0) calculated.yogurt = Math.round(flour * (recipe.yogurt / 100))
    if (recipe.molasses > 0) calculated.molasses = (flour * (recipe.molasses / 100)).toFixed(1)

    setIngredients(calculated)
  }

  const getDisplayUnit = (ingredient) => {
    const unitType = INGREDIENT_UNITS[ingredient]
    if (unitType === 'count') return ''
    if (useML) return 'ml'
    return 'g'
  }

  const getDisplayValue = (ingredient, gramsValue) => {
    // Convert grams to ml for display if useML is true
    const unitType = INGREDIENT_UNITS[ingredient]
    if (unitType === 'count') return gramsValue // eggs stay as count
    if (useML) {
      const density = DENSITIES[ingredient] || 1.0
      return parseFloat((gramsValue / density).toFixed(1))
    }
    return gramsValue
  }

  const getGramsFromDisplay = (ingredient, displayValue) => {
    // Convert display value back to grams if in ML mode
    const unitType = INGREDIENT_UNITS[ingredient]
    if (unitType === 'count') return displayValue // eggs stay as count
    if (useML) {
      const density = DENSITIES[ingredient] || 1.0
      return parseFloat((displayValue * density).toFixed(1))
    }
    return displayValue
  }

  const getIngredientValueForCalculation = (ingredient, displayValue) => {
    // Convert count (eggs) to weight for percentage calculations
    if (INGREDIENT_UNITS[ingredient] === 'count') {
      return displayValue * AVG_EGG_WEIGHT
    }
    // If we're in ML mode, convert back to grams for calculations
    return getGramsFromDisplay(ingredient, displayValue)
  }

  const adjustIngredient = (ingredient, displayValue) => {
    let roundedDisplayValue
    if (INGREDIENT_UNITS[ingredient] === 'count') {
      // For count ingredients (eggs), use whole numbers
      roundedDisplayValue = Math.round(parseFloat(displayValue))
    } else {
      roundedDisplayValue = Math.round(parseFloat(displayValue) * 10) / 10
    }

    // Convert display value to grams for storage
    const gramsValue = getGramsFromDisplay(ingredient, roundedDisplayValue)
    const newIngredients = { ...ingredients, [ingredient]: gramsValue }
    setIngredients(newIngredients)

    // Convert to weight for feedback
    const valueForFeedback = getIngredientValueForCalculation(ingredient, gramsValue)
    provideFeedback(ingredient, valueForFeedback)
  }

  const provideFeedback = (ingredient, value) => {
    const recipe = breadRecipes[selectedBread]
    const flour = ingredients.flour
    const feedbackMessages = []

    switch (ingredient) {
      case 'water': {
        const hydration = (value / flour) * 100
        const originalHydration = recipe.hydration

        if (hydration > originalHydration + 10) {
          feedbackMessages.push({
            type: 'warning',
            message: `High hydration (${hydration.toFixed(0)}%): Dough will be very sticky and wet. Expect larger holes in crumb, but harder to shape.`
          })
        } else if (hydration < originalHydration - 10) {
          feedbackMessages.push({
            type: 'warning',
            message: `Low hydration (${hydration.toFixed(0)}%): Dough will be stiff and dry. Bread may be dense with tight crumb.`
          })
        } else {
          feedbackMessages.push({
            type: 'success',
            message: `Good hydration (${hydration.toFixed(0)}%): Should produce balanced texture.`
          })
        }
        break
      }

      case 'yeast': {
        const yeastPercent = (value / flour) * 100
        const originalYeast = recipe.yeast

        if (yeastPercent > originalYeast * 1.5) {
          feedbackMessages.push({
            type: 'warning',
            message: 'High yeast: Faster rise but may have yeasty flavor. Watch for over-proofing.'
          })
        } else if (yeastPercent < originalYeast * 0.5) {
          feedbackMessages.push({
            type: 'info',
            message: 'Low yeast: Longer fermentation time. Better flavor development but requires patience.'
          })
        } else {
          feedbackMessages.push({
            type: 'success',
            message: 'Good yeast amount: Balanced rise and flavor.'
          })
        }
        break
      }

      case 'salt': {
        const saltPercent = (value / flour) * 100

        if (saltPercent > 2.5) {
          feedbackMessages.push({
            type: 'warning',
            message: 'High salt: May inhibit yeast activity and taste very salty.'
          })
        } else if (saltPercent < 1.5) {
          feedbackMessages.push({
            type: 'warning',
            message: 'Low salt: Bland flavor and weak gluten structure. Faster fermentation.'
          })
        } else {
          feedbackMessages.push({
            type: 'success',
            message: 'Good salt amount: Proper flavor and gluten strength.'
          })
        }
        break
      }

      case 'sugar': {
        const sugarPercent = (value / flour) * 100
        const originalSugar = recipe.sugar || 0

        if (sugarPercent > originalSugar + 5) {
          feedbackMessages.push({
            type: 'warning',
            message: 'High sugar: Sweeter bread, darker crust, softer crumb. May brown too quickly.'
          })
        } else if (sugarPercent < originalSugar - 3 && originalSugar > 0) {
          feedbackMessages.push({
            type: 'info',
            message: 'Low sugar: Less sweet, lighter crust color, firmer texture.'
          })
        }
        break
      }

      case 'butter':
      case 'oil': {
        const fatPercent = (value / flour) * 100
        const originalFat = (recipe[ingredient] || 0)

        if (fatPercent > originalFat + 10) {
          feedbackMessages.push({
            type: 'info',
            message: 'High fat: Richer flavor, softer crumb, longer shelf life. May need more mixing time.'
          })
        } else if (fatPercent < originalFat - 5 && originalFat > 0) {
          feedbackMessages.push({
            type: 'info',
            message: 'Low fat: Leaner bread, chewier texture, shorter shelf life.'
          })
        }
        break
      }
    }

    setFeedback(feedbackMessages)
  }

  return (
    <div className="app">
      <header className="header">
        <h1>🍞 Bread Calculator</h1>
      </header>

      <div className="container">
        <section className="bread-selector">
          <h2>Select Your Bread Type</h2>
          <div className="bread-grid">
            {Object.keys(breadRecipes).map((breadName) => (
              <button
                key={breadName}
                className={`bread-card ${selectedBread === breadName ? 'active' : ''}`}
                onClick={() => setSelectedBread(breadName)}
                style={{
                  borderColor: selectedBread === breadName ? breadRecipes[breadName].color : undefined
                }}
              >
                <h3>{breadName}</h3>
                <p>{breadRecipes[breadName].description}</p>
              </button>
            ))}
          </div>
        </section>

        <section className="calculator">
          <h2>Ingredients for {selectedBread}</h2>

          <div className="controls-group">
            <div className="flour-control">
              <label>
                <strong>Base Flour Amount:</strong>
                <input
                  type="range"
                  min="200"
                  max="1000"
                  step="50"
                  value={flourAmount}
                  onChange={(e) => setFlourAmount(parseInt(e.target.value))}
                />
                <span className="value">
                  {useML
                    ? `${(flourAmount / DENSITIES.flour).toFixed(1)}ml`
                    : `${flourAmount}g`}
                </span>
              </label>
            </div>

            <div className="unit-toggle">
              <label>
                <button
                  className={`toggle-btn ${!useML ? 'active' : ''}`}
                  onClick={() => setUseML(false)}
                >
                  g
                </button>
                <button
                  className={`toggle-btn ${useML ? 'active' : ''}`}
                  onClick={() => setUseML(true)}
                >
                  ml
                </button>
              </label>
            </div>
          </div>

          <div className="ingredients-list">
            {Object.entries(ingredients).map(([ingredient, gramsAmount]) => {
              const recipe = breadRecipes[selectedBread]
              const flour = ingredients.flour

              // Calculate base amount from recipe (in grams)
              let baseAmount = 0
              if (ingredient === 'water') baseAmount = flour * (recipe.hydration / 100)
              else if (ingredient === 'yeast') baseAmount = flour * (recipe.yeast / 100)
              else if (ingredient === 'salt') baseAmount = flour * (recipe.salt / 100)
              else if (ingredient === 'sugar') baseAmount = flour * (recipe.sugar / 100)
              else if (ingredient === 'butter') baseAmount = flour * (recipe.butter / 100)
              else if (ingredient === 'oil') baseAmount = flour * (recipe.oil / 100)
              else if (ingredient === 'eggs') baseAmount = flour * (recipe.eggs / 100)
              else if (ingredient === 'starter') baseAmount = flour * (recipe.starter / 100)
              else if (ingredient === 'yogurt') baseAmount = flour * (recipe.yogurt / 100)
              else if (ingredient === 'molasses') baseAmount = flour * (recipe.molasses / 100)

              const isCount = INGREDIENT_UNITS[ingredient] === 'count'

              // Convert to display values for min/max/step
              const minValueGrams = isCount ? 1 : (ingredient === 'water' ? baseAmount * 0.5 : baseAmount * 0.3)
              const maxValueGrams = isCount ? Math.max(10, baseAmount * 2) : (ingredient === 'water' ? baseAmount * 1.5 : baseAmount * 2)
              const minValue = getDisplayValue(ingredient, minValueGrams)
              const maxValue = getDisplayValue(ingredient, maxValueGrams)
              const stepValue = isCount ? 1 : (baseAmount > 10 ? 5 : 0.5)

              const displayValue = getDisplayValue(ingredient, gramsAmount)
              const unit = getDisplayUnit(ingredient)

              return (
                <div key={ingredient} className="ingredient-item">
                  <label>
                    <strong>{ingredient.charAt(0).toUpperCase() + ingredient.slice(1)}:</strong>
                    {ingredient === 'flour' ? (
                      <span className="value fixed">
                        {displayValue.toFixed(1)}{unit} (100%)
                      </span>
                    ) : (
                      <>
                        <input
                          type="range"
                          min={minValue}
                          max={maxValue}
                          step={stepValue}
                          value={displayValue}
                          onChange={(e) => adjustIngredient(ingredient, e.target.value)}
                        />
                        <span className="value">
                          {isCount ? (
                            `${displayValue} ${ingredient}${displayValue !== 1 ? 's' : ''} (${((getIngredientValueForCalculation(ingredient, gramsAmount) / ingredients.flour) * 100).toFixed(1)}%)`
                          ) : (
                            `${Number(displayValue).toFixed(1)}${unit} (${((gramsAmount / ingredients.flour) * 100).toFixed(1)}%)`
                          )}
                        </span>
                      </>
                    )}
                  </label>
                </div>
              )
            })}
          </div>

          {feedback.length > 0 && (
            <div className="feedback-section">
              <h3>💡 Ingredient Feedback</h3>
              {feedback.map((item, index) => (
                <div key={index} className={`feedback-item ${item.type}`}>
                  {item.message}
                </div>
              ))}
            </div>
          )}

          <div className="recipe-info">
            <h3>📝 Recipe Summary</h3>
            <p><strong>Total dough weight:</strong> {Object.values(ingredients).reduce((a, b) => parseFloat(a) + parseFloat(b), 0).toFixed(0)}g</p>
            <p><strong>Hydration:</strong> {((ingredients.water / ingredients.flour) * 100).toFixed(1)}%</p>
            <div className="steps">
              <strong>Steps:</strong>
              <ol>
                <li>Mix dry ingredients with water</li>
                <li>Knead until smooth and elastic</li>
                <li>First rise until doubled (1-2 hours)</li>
                <li>Shape dough into desired form</li>
                <li>Second rise until puffy (30-60 min)</li>
                <li>Bake at 375-450°F until golden</li>
              </ol>
            </div>
            <p className="tip">
              {breadRecipes[selectedBread].description}
            </p>
          </div>
        </section>
      </div>
    </div>
  )
}

export default App
